import React, { useState } from 'react';
import './App.css';

function App() {
  const [query, setQuery] = useState('');
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanding, setExpanding] = useState(false);
  const [expandedResponse, setExpandedResponse] = useState(null);
  const [googleResults, setGoogleResults] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [googleVideos, setGoogleVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [googleAIResponse, setGoogleAIResponse] = useState(null);
  const [googleAILoading, setGoogleAILoading] = useState(false);
  const [googleAIError, setGoogleAIError] = useState(null);
  const [showGoogleAI, setShowGoogleAI] = useState(false);

  // PWA install prompt state
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isInstallReady, setIsInstallReady] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  const fetchResponsesForQuery = async (q) => {
    setLoading(true);
    setError(null);
    setResponses(null);
    setExpandedResponse(null);
    // reset google results but keep in separate loading state
    setGoogleResults([]);
    setGoogleError(null);
    setGoogleVideos([]);
    setVideosError(null);

    try {
      // Read PDF content from session storage
      const sessionPdfContent = sessionStorage.getItem('pdfContent');
      const sessionPdfFileName = sessionStorage.getItem('pdfFileName');
      
      const requestBody = { 
        query: q.trim(),
        ...(sessionPdfContent && { documentContent: sessionPdfContent, documentName: sessionPdfFileName })
      };
      
      const response = await fetch('/api/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setResponses(data);
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }

    // Kick off Google searches in parallel (non-blocking UI)
    const searchQueries = [
      { endpoint: '/api/search/google', setter: setGoogleResults, setLoading: setGoogleLoading, setError: setGoogleError },
      { endpoint: '/api/search/google/videos', setter: setGoogleVideos, setLoading: setVideosLoading, setError: setVideosError }
    ];

    searchQueries.forEach(async ({ endpoint, setter, setLoading, setError }) => {
      try {
        setLoading(true);
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: q.trim(), limit: 5 })
        });
        if (!res.ok) throw new Error(`Search error ${res.status}`);
        const data = await res.json();
        setter(Array.isArray(data.results) ? data.results : []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      setUploadError('Please upload a PDF file only.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setUploadError('File size must be less than 10MB.');
      return;
    }
    
    setUploading(true);
    setUploadError(null);
    
    try {
      // Read PDF file using FileReader
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const arrayBuffer = event.target.result;
          
          // Use PDF.js to extract text
          const pdfjsLib = window.pdfjsLib;
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          let fullText = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n';
          }
          
          // Clean and truncate text
          let content = fullText.trim();
          if (content.length > 50000) {
            content = content.substring(0, 50000) + '... [Content truncated]';
          }
          
          // Store in session storage
          sessionStorage.setItem('pdfContent', content);
          sessionStorage.setItem('pdfFileName', file.name);
          
          setUploadedFile(file);
          setFileContent(content);
          setUploadError(null);
        } catch (err) {
          setUploadError('Failed to read PDF file: ' + err.message);
        } finally {
          setUploading(false);
        }
      };
      
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setUploadError(err.message);
      setUploading(false);
    }
  };

  const handleGoogleAISearch = async () => {
    if (!query.trim()) return;
    
    setGoogleAILoading(true);
    setGoogleAIError(null);
    
    try {
      const response = await fetch('/api/search/google/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: query.trim(),
          ...(fileContent && { documentContent: fileContent, documentName: uploadedFile?.name })
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Google AI search failed: ${response.status}`);
      }
      
      const data = await response.json();
      setGoogleAIResponse(data);
      setShowGoogleAI(true);
    } catch (err) {
      setGoogleAIError(err.message);
    } finally {
      setGoogleAILoading(false);
    }
  };

  const handleRefresh = () => {
    // Clear session storage and refresh page
    sessionStorage.removeItem('pdfContent');
    sessionStorage.removeItem('pdfFileName');
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    await fetchResponsesForQuery(query);
  };

  // Initialize PDF.js and load session storage on component mount
  React.useEffect(() => {
    // Initialize PDF.js
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }
    
    // Load PDF content from session storage if available
    const sessionPdfContent = sessionStorage.getItem('pdfContent');
    const sessionPdfFileName = sessionStorage.getItem('pdfFileName');
    
    if (sessionPdfContent && sessionPdfFileName) {
      setFileContent(sessionPdfContent);
      setUploadedFile({ name: sessionPdfFileName });
    }

    // Listen for PWA install events
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing
      e.preventDefault();
      setDeferredInstallPrompt(e);
      setIsInstallReady(true);
    };
    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setIsInstallReady(false);
      setDeferredInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Reset deep dive when the user changes the input query text
  React.useEffect(() => {
    setExpandedResponse(null);
  }, [query]);


  const handleCopyCombined = () => {
    if (responses && responses.combinedResponse) {
      const text = responses.combinedResponse.response || '';
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(text).catch(() => {});
      }
    }
  };

  const handleElaborate = async () => {
    if (!query.trim() || expanding) return;
    setExpanding(true);
    try {
      // If the current results don't match the input query, refresh them first
      if (!responses || (responses && responses.query !== query.trim())) {
        await fetchResponsesForQuery(query);
      }

      const elaborationPrompt = `${query.trim()}\n\nPlease provide a deeper, more comprehensive explanation. Include:\n- Clear definitions and key concepts\n- Practical examples and use cases\n- Step-by-step guidance or workflows\n- Comparisons, trade-offs, and caveats\n- Helpful tips and common pitfalls`;
      const response = await fetch('/api/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: elaborationPrompt })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const deepDive = data?.combinedResponse?.response || '';
      setExpandedResponse(deepDive);
    } catch (e) {
      // Surface inline error unobtrusively by reusing error banner
      setError(e.message || 'Failed to fetch elaboration');
    } finally {
      setExpanding(false);
    }
  };

  const handleInstallApp = async () => {
    try {
      if (!deferredInstallPrompt) return;
      deferredInstallPrompt.prompt();
      await deferredInstallPrompt.userChoice;
      setDeferredInstallPrompt(null);
      setIsInstallReady(false);
    } catch (_) {}
  };


  return (
    <div className="min-h-screen" style={{
      backgroundImage: 'url(/background.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: 'rgb(27, 27, 27)' // Fallback color
    }}>
      {/* Header */}
      <header className="shadow-sm border-b" style={{backgroundColor: 'rgba(27, 27, 27, 0.9)', borderBottomColor: '#aca793'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-center items-center">
            <img src="/logo192.png" alt="Logo" className="h-14" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Search Form */}
        <div className="rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-700">Search with Multi-AI</h2>
            <button
              onClick={handleRefresh}
              className="btn-brand py-2.5 px-3 sm:px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2"
              title="Start new conversation"
            >
              <span className="flex items-center gap-1">
                <span>+</span>
                <span>New Chat</span>
              </span>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {/* PDF Upload Section */}
            <div>
              <label htmlFor="pdf-upload" className="block text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                Upload PDF Document (Optional)
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  id="pdf-upload"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-white hover:file:bg-gray-700 disabled:opacity-50"
                />
                {uploading && (
                  <div className="text-sm text-gray-600">Processing...</div>
                )}
              </div>
              {uploadError && (
                <div className="mt-2 text-sm text-red-600">{uploadError}</div>
              )}
              {uploadedFile && (
                <div className="mt-2 text-sm text-green-600">
                  ✓ {uploadedFile.name} uploaded successfully
                </div>
              )}
            </div>

            <div>
              <label htmlFor="query" className="block text-lg sm:text-xl font-semibold text-gray-700 mb-2">
                Ask a question{fileContent ? <span className="text-sm font-normal"> about the document</span> : ''}
              </label>
              <input
                type="text"
                id="query"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={fileContent ? "Ask questions about the uploaded document..." : "What is artificial intelligence?"}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="w-full btn-brand py-3 px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Searching...' : `Search with Multi-AI${fileContent ? ' + Document' : ''}`}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
               <div className="border border-red-200 rounded-lg p-4 mb-6" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="rounded-lg shadow-md p-6 sm:p-8 text-center" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 mx-auto" style={{borderColor: 'rgb(27, 27, 27)'}}></div>
            <p className="mt-4 text-gray-600 text-sm sm:text-base">Querying AI models...</p>
          </div>
        )}

        {/* Results */}
        {responses && (
          <div className="space-y-5 sm:space-y-6">
            {/* Query Display */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Query</h2>
              <p className="text-gray-700">{responses.query}</p>
            </div>

            {/* Super AI Response */}
            {responses.combinedResponse && (
               <div className="rounded-xl shadow-lg border border-blue-200/60 p-0 overflow-hidden" style={{backgroundColor: '#ffffff'}}>
                 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-blue-100" style={{backgroundColor: '#aca793'}}>
                  <div>
                     <h2 className="text-lg sm:text-xl font-semibold text-gray-900">NexusAI Response</h2>
                     {fileContent && (
                       <p className="text-sm text-gray-600 mt-1">
                         📄 Analyzing: {uploadedFile?.name}
                       </p>
                     )}
                   </div>
                   <div className="flex items-center gap-2">
                     <button
                       type="button"
                       onClick={handleElaborate}
                       disabled={expanding}
                       className="btn-brand py-2.5 px-3 sm:px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       {expanding ? 'Elaborating…' : 'Elaborate'}
                     </button>
                     <button
                       type="button"
                       onClick={handleCopyCombined}
                       className="btn-brand py-2.5 px-3 sm:px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2"
                       aria-label="Copy response"
                     >
                       <span className="select-none">Copy</span>
                     </button>
                  </div>
                </div>
                
                 <div className="px-4 sm:px-6 py-5 sm:py-6">
                   <div className="prose max-w-none">
                     <p className="text-gray-900 text-base sm:text-lg leading-7 sm:leading-8 whitespace-pre-wrap">
                    {responses.combinedResponse.response}
                  </p>
                </div>

                   {expandedResponse && (
                     <div className="mt-6 sm:mt-8 border-t border-gray-200 pt-5 sm:pt-6">
                       <h3 className="text-base font-semibold text-gray-900 mb-3">Deep Dive</h3>
                       <div className="prose max-w-none">
                         <p className="text-gray-900 text-sm sm:text-base leading-6 sm:leading-7 whitespace-pre-wrap">
                           {expandedResponse}
                         </p>
                       </div>
                     </div>
                   )}

                   <div className="mt-5 sm:mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm">
                     <div className="flex items-center gap-3">
                       <span className="text-gray-600">Confidence</span>
                       <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 font-medium">
                      {(responses.combinedResponse.confidence * 100).toFixed(1)}%
                    </span>
                  </div>
                     <div className="text-gray-500">
                       Generated {new Date(responses.timestamp).toLocaleTimeString()}
                     </div>
                  </div>
                </div>
              </div>
            )}

            {/* Google Search Results - Hidden when document is uploaded */}
            {!fileContent && (
              <div className="space-y-4 sm:space-y-6">
                {/* Web Results */}
                <div className="rounded-lg p-4 sm:p-6 shadow-md border border-gray-200" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <h2 className="text-lg sm:text-xl font-semibold" style={{color: '#cd32c4ff'}}>
                      Google Search Results
                    </h2>
                    <div className="flex items-center gap-2">
                      {googleLoading && (
                        <span className="text-xs sm:text-sm" style={{color: 'rgb(27, 27, 27)'}}>Loading…</span>
                      )}
                      <button
                        onClick={handleGoogleAISearch}
                        disabled={googleAILoading || !query.trim()}
                        className="btn-brand py-2.5 px-3 sm:px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        title="Get AI-powered search insights"
                      >
                        {googleAILoading ? 'AI Searching...' : 'Google AI'}
                      </button>
                    </div>
                  </div>
                  {googleError && (
                    <div className="text-sm text-red-700 mb-2">{googleError}</div>
                  )}
                  {googleAIError && (
                    <div className="text-sm text-red-700 mb-2">{googleAIError}</div>
                  )}
                  
                  {showGoogleAI && googleAIResponse ? (
                    <div className="rounded-md p-4 border border-gray-300 bg-white">
                      <div className="flex items-center gap-2 mb-3">
                        <img 
                          src="/Google-AI-Logo-PNG.ico" 
                          alt="Google AI" 
                          className="w-5 h-5"
                        />
                        <h3 className="font-semibold text-gray-900">Google AI</h3>
                      </div>
                      <div className="prose max-w-none">
                        <p className="text-gray-900 text-base sm:text-lg leading-7 sm:leading-8 whitespace-pre-wrap">
                          {googleAIResponse.response}
                        </p>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => setShowGoogleAI(false)}
                          className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        >
                          ← Back to Regular Results
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {(!googleLoading && googleResults.length === 0 && !googleError) && (
                        <div className="text-sm" style={{color: 'rgb(27, 27, 27)'}}>No results found.</div>
                      )}
                      <ul className="space-y-2 sm:space-y-3">
                        {googleResults.map((it, idx) => (
                          <li key={idx} className="rounded-md p-3 border border-gray-300 bg-white">
                            <a href={it.link} target="_blank" rel="noreferrer" className="hover:underline font-medium break-words" style={{color: '#1b047bff'}}>
                              {it.title}
                            </a>
                            {it.snippet && (
                              <p className="mt-1 text-sm text-gray-700 break-words">{it.snippet}</p>
                            )}
                    </li>
                  ))}
                </ul>
                    </>
                  )}
                </div>

                {/* Videos Results */}
                <div className="rounded-lg p-4 sm:p-6 shadow-md border border-gray-200" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <h2 className="text-lg sm:text-xl font-semibold" style={{color: '#cd32c4ff'}}>
                      Related Videos
                    </h2>
                    {videosLoading && (
                      <span className="text-xs sm:text-sm" style={{color: 'rgb(27, 27, 27)'}}>Loading…</span>
                    )}
                  </div>
                  {videosError && (
                    <div className="text-sm text-red-700 mb-2">{videosError}</div>
                  )}
                  {(!videosLoading && googleVideos.length === 0 && !videosError) && (
                    <div className="text-sm" style={{color: 'rgb(27, 27, 27)'}}>No videos found.</div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {googleVideos.map((video, idx) => (
                      <div key={idx} className="rounded-md overflow-hidden border border-gray-300 bg-white">
                        <a href={video.videoUrl} target="_blank" rel="noreferrer">
                          <div className="relative">
                            {video.thumbnail && (
                              <img 
                                src={video.thumbnail} 
                                alt={video.title}
                                className="w-full h-32 sm:h-40 object-cover hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                              <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </a>
                        <div className="p-3">
                          <a href={video.videoUrl} target="_blank" rel="noreferrer" className="hover:underline font-medium text-sm break-words" style={{color: '#1b047bff'}}>
                            {video.title}
                          </a>
                          {video.snippet && (
                            <p className="mt-1 text-xs text-gray-700 line-clamp-2 break-words">{video.snippet}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Response Statistics */}
            <div className="backdrop-blur rounded-lg p-4 border border-gray-200" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-3">Response Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="rounded-md border border-gray-200 p-3" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="text-gray-500">Successful</div>
                  <div className="mt-1 font-semibold text-green-700">
                    {responses.individualResponses.length}
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 p-3" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="text-gray-500">Failed</div>
                  <div className="mt-1 font-semibold text-red-700">
                    {responses.failedRequests.length}
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 p-3" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="text-gray-500">Confidence</div>
                  <div className="mt-1 font-semibold" style={{color: '#1b047bff'}}>
                    {responses.combinedResponse ? (responses.combinedResponse.confidence * 100).toFixed(1) + '%' : 'N/A'}
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 p-3" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
                  <div className="text-gray-500">Timestamp</div>
                  <div className="mt-1 font-semibold text-gray-700">
                    {new Date(responses.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        {!responses && !loading && (
          <div className="border border-blue-200 rounded-lg p-4 sm:p-6" style={{backgroundColor: 'rgba(172, 167, 147, 1)'}}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base sm:text-lg font-medium" style={{color: 'rgb(27, 27, 27)'}}>How it works</h3>
              {isInstallReady && !isAppInstalled && (
                <button
                  type="button"
                  onClick={handleInstallApp}
                  className="btn-brand py-2.5 px-3 sm:px-4 rounded-lg focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 text-sm sm:text-base"
                  title="Install app"
                >
                  Install app
                </button>
              )}
            </div>
            <div className="space-y-2 sm:space-y-3" style={{color: 'rgb(27, 27, 27)'}}>
              <p><strong>Step 1:</strong> Optionally upload a PDF document to analyze (up to 10MB)</p>
              <p><strong>Step 2:</strong> Enter your question in the search box above</p>
              <p><strong>Step 3:</strong> The system queries multiple AI models simultaneously and generates a "NexusAI" response by combining the best answers using advanced text similarity algorithms</p>
              <p><strong>Step 4:</strong> You receive a unified, intelligent response that leverages the combined intelligence of multiple AI models, enhanced with document context if uploaded</p>
              <p><strong>Bonus:</strong> Get additional web search results and related videos for comprehensive information</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-12" style={{backgroundColor: 'rgba(27, 27, 27, 0.9)', borderTopColor: '#aca793'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm" style={{color: '#aca793'}}>
            Multi-AI Search Engine - Powered by Super AI Intelligence
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;